import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminCreateConsultantHoursTransaction,
  getAdminGetOrganizationQueryKey,
  getAdminGetConsultantHoursTransactionsQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { formatHours } from '@/lib/hours';
import { ConsultantHoursTransactionFields } from './ConsultantHoursTransactionFields';
import {
  consultantHoursTransactionSchema,
  type ConsultantHoursTransactionForm,
} from '@/schemas/consultant-hours';

interface RecordConsultantHoursDialogProps {
  organizationId: string;
  availableHours: number;
}

const defaultValues: ConsultantHoursTransactionForm = {
  type: 'Credit',
  hours: 0,
  description: '',
};

/**
 * Dialog for crediting hours to, or debiting consumed hours from, an organization's consultant
 * hours ledger. On success the organization (for its totals) and its ledger are refetched.
 */
export function RecordConsultantHoursDialog({
  organizationId,
  availableHours,
}: RecordConsultantHoursDialogProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<ConsultantHoursTransactionForm>({
    resolver: zodResolver(consultantHoursTransactionSchema),
    defaultValues,
  });
  const { handleSubmit, watch, reset } = form;

  const type = watch('type');

  const { mutate, isPending } = useAdminCreateConsultantHoursTransaction({
    mutation: {
      onSuccess: (_, variables) => {
        toast.success(
          `${variables.data.type === 'Credit' ? 'Credited' : 'Debited'} ${formatHours(variables.data.hours)}`
        );
        setIsOpen(false);
        reset(defaultValues);
        queryClient.invalidateQueries({
          queryKey: getAdminGetOrganizationQueryKey(organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetConsultantHoursTransactionsQueryKey(organizationId),
        });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'CreateConsultantHoursTransaction', orgId: organizationId });
        toast.error(
          'Failed to record consultant hours. ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const onSubmit = useCallback(
    (data: ConsultantHoursTransactionForm) => {
      const description = data.description.trim();
      mutate({
        orgId: organizationId,
        data: { type: data.type, hours: data.hours, description: description || undefined },
      });
    },
    [mutate, organizationId]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        reset(defaultValues);
      }
    },
    [reset]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Record Hours
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Consultant Hours</DialogTitle>
          <DialogDescription>
            Credit hours to this organization, or debit hours it has consumed. The organization's
            totals are always recalculated from these entries.
          </DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <ConsultantHoursTransactionFields
              debitHint={`${formatHours(availableHours)} available.`}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Saving...' : type === 'Credit' ? 'Credit Hours' : 'Debit Hours'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
